import React, { useState } from 'react';
import QuickActionMenu from './QuickActionMenu';
import environment from '../../environment';
import { stripCallMeWhen } from '../../methods/callMeWhen';

// Profile images are stored as blob names (users.image); the displayable URL is
// sasurl/container/<blob>. Some records may already hold a full URL — pass those through.
const resolveImg = (img: any): string => {
    if (!img) return '';
    if (/^https?:\/\//i.test(img) || String(img).startsWith('data:') || String(img).startsWith('/')) return img;
    return `${environment.sasurl}/${environment.container}/${img}`;
};

// cw: Registration List photolist card. Clones the FindMember card layout
// (src/Pages/FindMember/index.tsx L118-165). Every field renders ONLY when it is
// filled in the profile — no placeholders for blanks. `member` is one row from
// api/attendees/list (its `memberDetails` holds the full user doc).
type Props = {
    member: any;
    connectMeetLabel: string;
    testimonialLabel: string;
    canAct: boolean;
    busy?: boolean;
    onRequestCm: (member: any) => void;
    onRequestTy: (member: any) => void;
    // Selection mode (e.g. the CM "request specific people" picker): the whole card becomes a
    // toggle and shows a checkbox instead of the action menu.
    selectable?: boolean;
    selected?: boolean;
    onToggleSelect?: (member: any) => void;
};

const RegistrationCard: React.FC<Props> = ({
    member,
    connectMeetLabel,
    testimonialLabel,
    canAct,
    busy,
    onRequestCm,
    onRequestTy,
    selectable = false,
    selected = false,
    onToggleSelect,
}) => {
    const md = member?.memberDetails || {};
    const pd = md?.profileData || {};
    const [imgOk, setImgOk] = useState(true);

    const fullName = md.fullName || member.fullName || `${md.firstName || ''} ${md.lastName || ''}`.trim();
    const image = resolveImg(md.image);
    const bio = pd.headline || md.aboutUs;
    const callMeWhen = stripCallMeWhen(pd.callMeWhen);
    const company = md.company || md.companyName;
    const industry = pd.industry;
    const profession = md.profession;
    const specialties: string[] = Array.isArray(pd.specialties) ? pd.specialties : [];
    const email = md.email || member.email;
    const secondaryEmail = md.secondaryEmail;
    const phone = md.mobileNo ? `${md.dialCode ? '+' + String(md.dialCode).replace('+', '') + ' ' : ''}${md.mobileNo}` : '';
    const linkedInUrl = md.linkedInUrl;
    const roleTag = member.type && member.type !== 'Member' && member.type !== 'guest' ? member.type : '';

    return (
        <div
            className={`bg-white rounded-lg shadow-box p-4 flex items-start gap-4 ${
                selectable && selected ? 'ring-2 ring-orange-500 border border-orange-300' : ''
            }`}
        >
            {selectable && (
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleSelect && onToggleSelect(member)}
                    title="Select this person"
                    className="h-5 w-5 mt-4 flex-shrink-0 accent-orange-500 cursor-pointer"
                />
            )}
            <div className="w-14 h-14 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                {image && imgOk ? (
                    <img src={image} alt="" onError={() => setImgOk(false)} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-lg font-bold">
                        {(fullName || '?')[0].toUpperCase()}
                    </div>
                )}
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900 capitalize">{fullName}</span>
                            {roleTag && (
                                <span className="text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{roleTag}</span>
                            )}
                        </div>
                        {bio && <p className="text-sm text-gray-600 mt-0.5">{bio}</p>}
                    </div>
                    {!selectable && canAct && (
                        <QuickActionMenu
                            connectMeetLabel={connectMeetLabel}
                            testimonialLabel={testimonialLabel}
                            mode="event"
                            busy={busy}
                            onRequestCm={() => onRequestCm(member)}
                            onRequestTy={() => onRequestTy(member)}
                        />
                    )}
                </div>

                {(industry || company || profession) && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                        {industry && <span>{industry}</span>}
                        {company && <span>{company}</span>}
                        {profession && <span>{profession}</span>}
                    </div>
                )}

                {specialties.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                        {specialties.slice(0, 6).map((s: string, i: number) => (
                            <span key={i} className="bg-orange-50 text-orange-700 text-xs px-2 py-0.5 rounded-full">
                                {s}
                            </span>
                        ))}
                    </div>
                )}

                {callMeWhen && (
                    <p className="text-xs text-gray-500 mt-1 italic">Call me when: {callMeWhen}</p>
                )}

                {(email || secondaryEmail || phone || linkedInUrl) && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                        {email && <a href={`mailto:${email}`} className="text-blue-600 hover:underline">{email}</a>}
                        {secondaryEmail && <a href={`mailto:${secondaryEmail}`} className="text-blue-600 hover:underline">{secondaryEmail}</a>}
                        {phone && <span>{phone}</span>}
                        {linkedInUrl && <a href={linkedInUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">LinkedIn</a>}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegistrationCard;
